import { Processing } from '@paywithextend/components'
import queryString from 'query-string'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BillPayBox, { CheckboxProps } from 'src/components/BillPayBox/BillPayBox'
import BoxAndLinks from 'src/components/BoxAndLinks/BoxAndLinks'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import SvgDownload from 'src/components/Svgs/SvgDownload'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import {
  OrganizationRoles,
  VirtualCardFilterStatus,
  VirtualCardTypes,
} from 'src/enums'
import {
  useQueryParamArrayHandler,
  useSortable,
  useStringSetState,
} from 'src/hooks'
import Trns from 'src/localization/Trns'
import {
  VirtualCardListModel,
  VirtualCardStatus,
} from 'src/models/virtualCard.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryVirtualCards } from 'src/queries/virtualCards.queries'
import { SearchQueryParams } from 'src/redux/createRootReducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectReportDownloadLoadingFromState } from 'src/redux/selectors/data/reportDownloads.selectors'
import {
  selectIsLeftFlyoutOpen,
  selectIsRightFlyoutOpen,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

// import useAnalyticsContext from 'src/analytics/useAnalyticsContext'

export interface BillPayFilters {
  keyword: string
  sortBy?: string
  sortDescending?: boolean
  statuses?: VirtualCardStatus[]
  deactivatingIn?: string
  cardholders?: string[]
  creditCardId?: string[]
}
export interface BillPayAnalyticsEventProperties {
  status?: string[]
  search?: string
  deactivating_in?: string
  cardholder?: string[]
  creditCardId?: string[]
}

type BillPayBoxAndLinksProps = {
  onDownloadClick?: (filters: VirtualCardsFiltersBody) => void
  defaultTitle: React.ReactElement
  virtualCards: VirtualCardListModel[]
  navAway?: NavAwayProps
  emptyStateDesign: TableEmptyStateDesigns
  isYourCards?: boolean
  hideFilters?: boolean
  initialFilterValues?: Partial<BillPayFilters>
  usersFullPaginatedData: UsersFullPaginatedDataState
  totalCount: number
  readOnly?: boolean
  checkboxProps?: CheckboxProps
  setSelectedFilters?: (filters: BillPayAnalyticsEventProperties) => void
  clientHeight?: number
}

const BillPayBoxAndLinks: React.FC<BillPayBoxAndLinksProps> = (
  props,
): React.ReactElement => {
  const loading = useSelector(selectReportDownloadLoadingFromState)
  // const { analytics } = useAnalyticsContext()
  const queryParams = useSelector(selectQueryParams)
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest

  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)

  const initialFilterValues: BillPayFilters = useMemo(
    () => ({
      keyword: '',
      deactivatingIn: '',
      cardholder: [],
      creditCardId: [],
      sortBy: 'updatedAt',
      sortDescending: true,
      ...props.initialFilterValues,
    }),
    [props.initialFilterValues],
  )

  const calculatedStatusFilter = (statuses: string[]): string[] => {
    const statusFilter = []

    // assumes query params will come in the expected combinations
    if (statuses?.some((status) => status === VirtualCardStatus.Active)) {
      statusFilter.push(VirtualCardFilterStatus.active)
    }
    if (statuses?.some((status) => status === VirtualCardStatus.Pending)) {
      statusFilter.push(VirtualCardFilterStatus.pending)
    }
    if (
      statuses?.some((status) => status === VirtualCardStatus.Cancelled) &&
      statuses?.some((status) => status === VirtualCardStatus.Expired) &&
      statuses?.some((status) => status === VirtualCardStatus.Rejected)
    ) {
      statusFilter.push(VirtualCardFilterStatus.inactive)
    }
    if (
      statuses?.some((status) => status === VirtualCardStatus.Closed) &&
      statuses?.some((status) => status === VirtualCardStatus.Consumed)
    ) {
      statusFilter.push(VirtualCardFilterStatus.closed)
    }
    return statusFilter
  }

  const [keywordFilter, setKeywordFilter] = useState(
    initialFilterValues.keyword,
  )
  const {
    set: statusesFilter,
    setSet: setStatusesFilter,
    handleSelection: handleStatusSelection,
  } = useStringSetState(
    new Set<string>(initialFilterValues.statuses) ?? new Set<string>(),
  )

  const { setSort, sortBy, sortDescending } = useSortable(
    initialFilterValues.sortBy,
    initialFilterValues.sortDescending,
  )
  const [deactivatingInFilter, setDeactivatingInFilter] = useState(
    initialFilterValues.deactivatingIn,
  )

  const calculatedStatuses = (
    statusesFilter: Set<string>,
  ): VirtualCardStatus[] => {
    const filteredStatuses = []
    if (statusesFilter.size > 0) {
      if (
        statusesFilter.has(VirtualCardFilterStatus.active) ||
        statusesFilter.has(VirtualCardStatus.Active)
      ) {
        filteredStatuses.push(VirtualCardStatus.Active)
      }
      if (
        statusesFilter.has(VirtualCardFilterStatus.pending) ||
        statusesFilter.has(VirtualCardStatus.Pending)
      ) {
        filteredStatuses.push(VirtualCardStatus.Pending)
      }
      if (
        statusesFilter.has(VirtualCardFilterStatus.inactive) ||
        statusesFilter.has(VirtualCardStatus.Cancelled) ||
        statusesFilter.has(VirtualCardStatus.Expired) ||
        statusesFilter.has(VirtualCardStatus.Rejected)
      ) {
        filteredStatuses.push(
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Expired,
          VirtualCardStatus.Rejected,
        )
      }
      if (
        statusesFilter.has(VirtualCardFilterStatus.closed) ||
        statusesFilter.has(VirtualCardStatus.Closed) ||
        statusesFilter.has(VirtualCardStatus.Consumed)
      ) {
        filteredStatuses.push(
          VirtualCardStatus.Closed,
          VirtualCardStatus.Consumed,
        )
      }
    }
    return filteredStatuses
  }

  const {
    set: cardholdersFilter,
    setSet: setCardholdersFilter,
    handleSelection: handleCardholderSelection,
  } = useStringSetState(
    initialFilterValues.cardholders
      ? new Set<string>(initialFilterValues.cardholders)
      : new Set<string>(),
  )

  const {
    set: creditCardsFilter,
    setSet: setCreditCardsFilter,
    handleSelection: handleCreditCardSelection,
  } = useStringSetState(
    initialFilterValues.creditCardId
      ? new Set<string>(initialFilterValues.creditCardId)
      : new Set<string>(),
  )

  const filtersBody: VirtualCardsFiltersBody = {
    search: keywordFilter ? `${keywordFilter}` : undefined,
    statuses: statusesFilter
      ? Array.from(calculatedStatuses(statusesFilter))
      : undefined,
    deactivatingIn: deactivatingInFilter
      ? `${deactivatingInFilter}`
      : undefined,
    cardholders: cardholdersFilter ? Array.from(cardholdersFilter) : undefined,
    creditCardIds:
      creditCardsFilter.size > 0 ? Array.from(creditCardsFilter) : undefined,
    sortField: sortBy,
    sortDirection: sortDescending === false ? 'ASC' : 'DESC',
  }

  const { data, fetchNextPage, isFetching, complete, hasNextPage } =
    useQueryVirtualCards({
      filters: { ...filtersBody, cardTypes: [VirtualCardTypes.billPay] },
    })

  const infiniteScrollProps: InfiniteScrollProps<VirtualCardsFiltersBody> = {
    fetch: () => fetchNextPage(),
    loading: isFetching,
    complete,
    hasNextPage,
    totalItems: data?.totalItems,
  }

  const paramString = queryString.stringify({
    search: filtersBody.search,
    recipients: filtersBody.recipients,
    statuses: filtersBody.statuses,
    deactivatingIn: filtersBody.deactivatingIn,
    cardholders: filtersBody.cardholders,
    creditCardId: filtersBody.creditCardIds,
  } satisfies SearchQueryParams)

  const pushUrlState = (): void => {
    window.history.pushState(
      JSON.stringify(null),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  const replaceUrlState = (): void => {
    window.history.replaceState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  // restores query params on flyout close
  useEffect((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      replaceUrlState()
    }
  }, [isRightFlyoutOpen, isLeftFlyoutOpen])

  useEffect((): void => {
    if (pushNewSearchState) {
      pushUrlState()
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  useEffect((): void => {
    props.setSelectedFilters !== undefined &&
      props.setSelectedFilters({
        status:
          statusesFilter.size > 0 ? Array.from(statusesFilter) : undefined,
        search: keywordFilter,
        deactivating_in: deactivatingInFilter,
        cardholder:
          cardholdersFilter.size > 0
            ? Array.from(cardholdersFilter)
            : undefined,
        creditCardId:
          creditCardsFilter.size > 0
            ? Array.from(creditCardsFilter)
            : undefined,
      })
  }, [
    keywordFilter,
    statusesFilter,
    deactivatingInFilter,
    cardholdersFilter,
    creditCardsFilter,
    sortBy,
    sortDescending,
  ])

  useEffect((): void => {
    setKeywordFilter(queryParams.search ? queryParams.search : '')
    setStatusesFilter(
      new Set(
        calculatedStatusFilter(
          useQueryParamArrayHandler(queryParams.statuses) as string[],
        ),
      ),
    )
    setDeactivatingInFilter(
      queryParams.deactivatingIn ? queryParams.deactivatingIn : '',
    )
    setCardholdersFilter(
      new Set(useQueryParamArrayHandler(queryParams.cardholders) as string[]),
    )
    setCreditCardsFilter(
      new Set(useQueryParamArrayHandler(queryParams.creditCardId) as string[]),
    )
  }, [queryParams])

  useEffect((): void => {
    // TOOD: search analytics
    // if (keywordFilter !== '') {
    //   analytics?.track(
    //     handlePageLocation() + ' virtual cards filter - search cards - typing', {
    //       intent: 'filter cards',
    //       screen: calculatedBackgroundLocation.pathname,
    //       interaction: 'typing',
    //       results: infiniteScrollProps.totalItems || 0,
    //       search: keywordFilter,
    //     },
    //   )
    // }
  }, [infiniteScrollProps.totalItems || keywordFilter])

  return (
    <BoxAndLinks
      links={
        props.onDownloadClick !== undefined && !isGuestUser
          ? [
              <HoverableLink
                key={0}
                css={css`
                  display: flex;
                  align-items: center;
                `}
                onClick={(): void => props.onDownloadClick?.(filtersBody)}
                ariaLabelSource={'Download All Virtual Cards (CSV)'}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div
                      css={css`
                        margin-right: 5px;
                      `}
                    >
                      <Trns source={'Processing...'} />
                    </div>
                    <Processing />
                  </>
                ) : (
                  <>
                    <Trns source={'Download All Virtual Cards (CSV)'} />
                    <SvgDownload
                      css={css`
                        transform: scale(calc(14 / 24));
                      `}
                    />
                  </>
                )}
              </HoverableLink>,
            ]
          : undefined
      }
    >
      <BillPayBox
        {...props}
        virtualCards={data?.virtualCards ?? []}
        loading={infiniteScrollProps.loading}
        complete={infiniteScrollProps.complete}
        readOnly={!!props.readOnly}
        fullProps={{
          keywordFilter: keywordFilter,
          setKeywordFilter: setKeywordFilter,
          statusesFilter: Array.from(statusesFilter),
          setStatusesFilter: setStatusesFilter,
          handleStatusSelection: handleStatusSelection,
          setPushNewSearchState: setPushNewSearchState,
          deactivatingInFilter: deactivatingInFilter,
          setDeactivatingInFilter: setDeactivatingInFilter,
          cardholdersFilter: Array.from(cardholdersFilter),
          setCardholdersFilter: setCardholdersFilter,
          handleCardholderSelection: handleCardholderSelection,
          creditCardsFilter: Array.from(creditCardsFilter),
          setCreditCardsFilter: setCreditCardsFilter,
          handleCreditCardSelection: handleCreditCardSelection,
          sortableProps: {
            setSort,
            sortBy,
            sortDescending: sortDescending,
          },
          filteredCount: infiniteScrollProps.totalItems,
        }}
        isYourCards={props.isYourCards}
        hideFilters={props.hideFilters}
        emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
        usersFullPaginatedData={props.usersFullPaginatedData}
        checkboxProps={props.checkboxProps}
        infiniteScrollProps={infiniteScrollProps}
        filtersBody={filtersBody}
        clientHeight={props.clientHeight}
      />
    </BoxAndLinks>
  )
}

export default BillPayBoxAndLinks
